// src/models/UserModel.js
import { normalizeRole, ROLES } from "../utils/roles";
import { sanitizeEmail } from "../utils/validation";

export class UserModel {
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "email",
    "name",
    "role",
    "companyId",
    "departmentId",
    "locationCode",
    "description",
    "status",
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "updatedAt",
    "updatedBy",
    "createdBy",
    "deletedFlag",
    "modifiedAt",
    "modifiedBy",
  ]);

     constructor(data = {}, { isCreation = false, createdByRole = null } = {}) {
    this._isCreation = isCreation;
    this._createdByRole = createdByRole || data.createdByRole || null;

    if (isCreation) {
      // 1. Creation: initialize defaults
      this.id =
        data.id ||
        data.uid ||
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()));
      this.email = sanitizeEmail(data.email || "");
      this.name = data.name || data.displayName || "";
      this.role = normalizeRole(data.role || ROLES.USER);
      this.companyId = data.companyId || null;
      this.departmentId = data.departmentId || null;
      this.locationCode = data.locationCode || null;

      this.description = data.description || data.Description || null;
      this.status =
        data.status ?? (data.isActive === false ? "inactive" : "active");

      const nowIso = new Date().toISOString();
      const creator =
        data.metadata?.createdBy || data.createdBy || data.email || "system";

      this.metadata = {
        createdAt: data.metadata?.createdAt || data.createdAt || nowIso,
        createdBy: creator,
        deletedFlag: data.metadata?.deletedFlag ?? false,
        modifiedAt:
          data.metadata?.modifiedAt ||
          data.updatedAt ||
          data.createdAt ||
          nowIso,
        modifiedBy:
          data.metadata?.modifiedBy ||
          data.updatedBy ||
          data.email ||
          creator,
        updatedAt:
          data.metadata?.updatedAt ||
          data.updatedAt ||
          data.metadata?.modifiedAt ||
          nowIso,
        updatedBy:
          data.metadata?.updatedBy ||
          data.updatedBy ||
          data.metadata?.modifiedBy ||
          creator,
      };
    } else {
      // 2. Update: only assign provided fields to avoid overwriting existing data
      for (const key of Object.keys(data)) {
        if (!UserModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...(data.metadata || {}) };
        } else if (key === "email") {
          this.email = sanitizeEmail(data.email || "");
        } else if (key === "role") {
          this.role = normalizeRole(data.role);
        } else if (key === "status") {
          this.status =
            data.status ?? (data.isActive === false ? "inactive" : "active");
        } else {
          this[key] = data[key];
        }
      }

      if (data.isActive !== undefined && data.status === undefined) {
        this.status = data.isActive === false ? "inactive" : "active";
      }

      if (data.updatedAt || data.modifiedAt || data.updatedBy || data.modifiedBy) {
        this.metadata = {
          ...(this.metadata || {}),
          ...(data.updatedAt || data.modifiedAt
            ? {
                updatedAt: data.updatedAt || data.modifiedAt,
                modifiedAt: data.modifiedAt || data.updatedAt,
              }
            : {}),
          ...(data.updatedBy || data.modifiedBy
            ? {
                updatedBy: data.updatedBy || data.modifiedBy,
                modifiedBy: data.modifiedBy || data.updatedBy,
              }
            : {}),
        };
      }
    }
  }

  isValid({ isCreation = this._isCreation } = {}) {
    // 1. Validation for new user creation
    if (isCreation) {
      if (
        !this.email ||
        typeof this.email !== "string" ||
        !this.email.includes("@")
      ) {
        return false;
      }

      if (!this.role) return false;

         if (
        !this.companyId ||
        typeof this.companyId !== "string" ||
        this.companyId.trim() === ""
      ) {
        return false;
      }

          // locationCode is optional if the *caller* (creator) is a superadmin,
      // or if the *target* user is a superadmin.
      const isCallerSuperAdmin = normalizeRole(this._createdByRole) === ROLES.SUPERADMIN;
      if (this.role !== ROLES.SUPERADMIN && !isCallerSuperAdmin) {
        if (
          !this.locationCode ||
          typeof this.locationCode !== "string" ||
          this.locationCode.trim() === ""
        ) {
          return false;
        }
      }

      // departmentId is optional under the same conditions.
      if (this.role !== ROLES.SUPERADMIN && !isCallerSuperAdmin) {
        if (
          !this.departmentId ||
          typeof this.departmentId !== "string" ||
          this.departmentId.trim() === ""
        ) {
          return false;
        }
      }
      if (!["active", "inactive"].includes(this.status)) return false;

      return true;
    }

    // 2. Validation for partial updates (only validate present fields)
    if (this.email !== undefined) {
      if (
        typeof this.email !== "string" ||
        !this.email.includes("@")
      ) {
        return false;
      }
    }

    if (this.companyId !== undefined) {
      if (typeof this.companyId !== "string" || this.companyId.trim() === "") {
        return false;
      }
    }

    // On updates, `null` is a legitimate "cleared" value for the optional
    // FK fields. Only validate shape when a non-null value is present.
    if (this.locationCode !== undefined && this.locationCode !== null) {
      if (
        typeof this.locationCode !== "string" ||
        this.locationCode.trim() === ""
      ) {
        return false;
      }
    }

    if (this.departmentId !== undefined && this.departmentId !== null) {
      if (
        typeof this.departmentId !== "string" ||
        this.departmentId.trim() === ""
      ) {
        return false;
      }
    }

    if (this.status !== undefined) {
      if (!["active", "inactive"].includes(this.status)) return false;
    }

    return true;
  }
  /**
   * Persistence-ready plain object with only allowed keys.
   */
  toPayload() {
    const payload = {};
    for (const key of UserModel.ALLOWED_KEYS) {
      if (this[key] === undefined) continue;

      if (key === "metadata") {
        payload.metadata = {};
        for (const metaKey of UserModel.ALLOWED_METADATA_KEYS) {
          if (this.metadata && this.metadata[metaKey] !== undefined) {
            payload.metadata[metaKey] = this.metadata[metaKey];
          }
        }
      } else {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}